import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { t } from 'i18next'
import ProfileActionTile from './ProfileActionTile'
import { Work, AccessTime } from '@mui/icons-material'
import { getSidebarState } from '../../store/redux/reducers/sidebarSlice'
import { useSelector } from 'react-redux'
import { User } from '../../store/types'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import navigation from '../../config/navigation'
import { useNavigate } from 'react-router-dom'
import ReportType from '../../store/types/enums/ReportType'
import GenerateReportPopup from '../../components/Popup/GenerateReportPopup'
import { getCurrentHideSchedule } from '../../store/redux/reducers/settingsSlice'

type ProfileViewActionsProps = {
  user: User
  isMyProfile: boolean
  isManagement: boolean
  displayTitle?: boolean
  isCurrentUserLeader: boolean | undefined
}

const ProfileViewActions = ({
  user,
  isMyProfile,
  isManagement,
  displayTitle = false,
  isCurrentUserLeader,
}: ProfileViewActionsProps) => {
  const theme = useTheme()
  const hideSchedule = useSelector(getCurrentHideSchedule)

  const isBetweenSmAndSmMid = useMediaQuery(theme.breakpoints.between('sm', 'smMid'))
  const isDownXsMid = useMediaQuery(theme.breakpoints.down('xsMid'))
  const isSidebarOpen = useSelector(getSidebarState)
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'))

  const currentUser = useSelector(getCurrentUser)

  const navigate = useNavigate()

  return (
    <Stack direction="column" px={{ xs: 2, sm: isSidebarOpen ? 2 : 2, md: 0 }} gap={{ xs: '16px' }}>
      {displayTitle && <Typography variant="h5">{t('page.usersProfile.form.headers.actions')}</Typography>}

      {isDownXsMid || (isBetweenSmAndSmMid && isSidebarOpen) ? (
        <Stack direction="row" justifyContent="center" alignItems="center" gap="24px">
          <ProfileActionTile
            Icon={Work}
            title={t('page.usersProfile.buttons.worklog')}
            handleClick={() => navigate(`${navigation.worklog}/${user.id}`)}
            disabled={!(user && currentUser && (isManagement || isMyProfile || isCurrentUserLeader))}
          />
          {(!hideSchedule || !isMyProfile) && (
            <ProfileActionTile
              Icon={AccessTime}
              title={t('page.usersProfile.buttons.schedule')}
              handleClick={() => navigate(`${navigation.schedule}/${user.id}`)}
              disabled={!(user && currentUser && (isManagement || isMyProfile || isCurrentUserLeader))}
            />
          )}
          <GenerateReportPopup
            selectedData={[{ id: user.id, leaderIds: user.leaders?.map(leader => leader.id) }]}
            reportType={ReportType.Employee}
            profileView={true}
            profileActionTile={true}
          />
        </Stack>
      ) : (
        <Stack direction="row" gap={'16px'}>
          <Button
            startIcon={<Work />}
            onClick={() => navigate(`${navigation.worklog}/${user.id}`)}
            color="primary"
            variant="contained"
            size="small"
            sx={{ width: isMd ? '100px' : '33%', span: { marginRight: isMd ? 0 : '8px' } }}
            disabled={!(user && currentUser && (isManagement || isMyProfile || isCurrentUserLeader))}
          >
            {t('page.usersProfile.buttons.worklog')}
          </Button>
          {(!hideSchedule || !isMyProfile) && (
            <Button
              startIcon={<AccessTime />}
              onClick={() => navigate(`${navigation.schedule}/${user.id}`)}
              color="primary"
              variant="contained"
              size="small"
              sx={{ width: isMd ? '100px' : '33%', span: { marginRight: isMd ? 0 : '8px' } }}
              disabled={!(user && currentUser && (isManagement || isMyProfile || isCurrentUserLeader))}
            >
              {t('page.usersProfile.buttons.schedule')}
            </Button>
          )}

          {(currentUser?.id === user.id || isManagement || isCurrentUserLeader) && (
            <GenerateReportPopup
              selectedData={[{ id: user.id, leaderIds: user.leaders?.map(leader => leader.id) }]}
              reportType={ReportType.Employee}
              profileView={true}
            />
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default ProfileViewActions
